import { api } from '@/shared/axios-client';
import { queryOptions, useQuery } from '@tanstack/react-query';

export type CoverageHistoryMode = 'combined' | 'automation' | 'manual';

export interface CoverageHistorySummaryBlock {
  totalCoverage?: { percentage?: number; covered?: number; total?: number };
}

export interface CoverageHistorySummary {
  linesOfCode?: CoverageHistorySummaryBlock;
  inventory?: CoverageHistorySummaryBlock;
}

export interface CoverageHistoryDashboardMetric {
  percentage?: number;
  covered?: number;
  total?: number;
  uncovered?: number;
  testCount?: number;
}

export interface CoverageHistoryDashboard {
  totalCoverage?: CoverageHistoryDashboardMetric;
  testAutomation?: CoverageHistoryDashboardMetric;
  manualTests?: CoverageHistoryDashboardMetric;
  uncovered?: CoverageHistoryDashboardMetric;
  inventoryTotalCoverage?: CoverageHistoryDashboardMetric;
  inventoryTestAutomation?: CoverageHistoryDashboardMetric;
  inventoryManualTests?: CoverageHistoryDashboardMetric;
  inventoryUncovered?: CoverageHistoryDashboardMetric;
  executableLines?: number;
  boilerplateLines?: number;
  totalLines?: number;
  coveredExecutableLines?: number;
  executableLinesInCoverage?: number;
  totalFiles?: number;
}

export interface CoverageHistoryTrendPoint {
  scanId?: string;
  scanNumber?: number;
  buildId?: string;
  buildNumber?: string | number;
  runId?: string | null;
  runNumber?: number | null;
  sourceType?: string;
  sessionId?: string;
  processedAt?: string;
  summary?: CoverageHistorySummary;
  dashboard?: CoverageHistoryDashboard;
}

export interface CoverageHistoryCompareResult {
  previous?: CoverageHistoryTrendPoint | null;
  latest?: CoverageHistoryTrendPoint | null;
  delta?: {
    linesOfCodePercentage?: number | null;
    inventoryPercentage?: number | null;
  };
}

interface ServiceEnvelope<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

export const fetchCoverageHistoryTrendBranch = async (
  repoId: string,
  branchId: string,
  mode: CoverageHistoryMode,
): Promise<CoverageHistoryTrendPoint[]> => {
  const res = (await api.get('/api/coverage/history/trend/branch', {
    params: { repoId, branchId, mode },
  })) as ServiceEnvelope<CoverageHistoryTrendPoint[]>;
  return res.data ?? [];
};

export const fetchCoverageHistoryCompareBranch = async (
  repoId: string,
  branchId: string,
  mode: CoverageHistoryMode,
): Promise<CoverageHistoryCompareResult> => {
  const res = (await api.get('/api/coverage/history/compare/branch', {
    params: { repoId, branchId, mode },
  })) as ServiceEnvelope<CoverageHistoryCompareResult>;
  return res.data ?? {};
};

export const coverageHistoryTrendBranchQueryOptions = (
  repoId: string,
  branchId: string,
  mode: CoverageHistoryMode,
) =>
  queryOptions({
    queryKey: ['coverage-history-trend-branch', repoId, branchId, mode],
    queryFn: () => fetchCoverageHistoryTrendBranch(repoId, branchId, mode),
    enabled: Boolean(repoId && branchId),
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useCoverageHistoryTrendBranch = (
  repoId: string,
  branchId: string,
  mode: CoverageHistoryMode,
) => useQuery(coverageHistoryTrendBranchQueryOptions(repoId, branchId, mode));

export interface CoverageHistoryRunListItem {
  runId?: string | null;
  runNumber?: number | null;
  processedAt?: string;
  sourceType?: string;
  sessionId?: string;
  summary?: CoverageHistorySummary;
}

export const fetchCoverageHistoryRuns = async (
  scanId: string,
  mode: CoverageHistoryMode,
): Promise<CoverageHistoryRunListItem[]> => {
  const res = (await api.get('/api/coverage/history/runs', {
    params: { scanId, mode },
  })) as ServiceEnvelope<CoverageHistoryRunListItem[]>;
  return res.data ?? [];
};

export const fetchCoverageHistoryTrendRuns = async (
  scanId: string,
  mode: CoverageHistoryMode,
): Promise<CoverageHistoryTrendPoint[]> => {
  const res = (await api.get('/api/coverage/history/trend/runs', {
    params: { scanId, mode },
  })) as ServiceEnvelope<CoverageHistoryTrendPoint[]>;
  return res.data ?? [];
};

export const coverageHistoryTrendRunsQueryOptions = (
  scanId: string,
  mode: CoverageHistoryMode,
) =>
  queryOptions({
    queryKey: ['coverage-history-trend-runs', scanId, mode],
    queryFn: () => fetchCoverageHistoryTrendRuns(scanId, mode),
    enabled: Boolean(scanId),
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useCoverageHistoryTrendRuns = (
  scanId: string,
  mode: CoverageHistoryMode,
) => useQuery(coverageHistoryTrendRunsQueryOptions(scanId, mode));
